import React from 'react';
import { TrendingUp, Users, Zap, Shield, BarChart3, FileText, ArrowRight } from 'lucide-react';
import Button from '../../../components/ui/Button';

const FeatureActivationCards = ({ plan, onExploreFeature }) => {
  const features = [
    {
      icon: Zap,
      title: 'Unlimited Symptom Tracking',
      description: 'Track symptoms without any daily or monthly limits',
      path: '/daily-check-in',
      color: 'blue',
      available: true
    },
    {
      icon: TrendingUp,
      title: 'Advanced Analytics',
      description: 'Access detailed insights, pattern analysis, and predictive trends',
      path: '/advanced-symptom-analytics',
      color: 'purple',
      available: plan?.features?.advanced_analytics
    },
    {
      icon: Users,
      title: 'Caregiver Management',
      description: `Connect with ${plan?.limits?.caregiver_connections === -1 ? 'unlimited' : plan?.limits?.caregiver_connections} caregiver${plan?.limits?.caregiver_connections !== 1 ? 's' : ''} and share your health data`,
      path: '/user-relationship-management',
      color: 'green',
      available: plan?.features?.caregiver_management
    },
    {
      icon: Shield,
      title: 'Priority Support',
      description: 'Get faster response times and dedicated assistance',
      path: '/settings-and-privacy',
      color: 'orange',
      available: plan?.features?.priority_support
    },
    {
      icon: BarChart3,
      title: 'Extended History',
      description: `Access ${plan?.limits?.symptom_history_days === -1 ? 'unlimited' : plan?.limits?.symptom_history_days + ' days of'} symptom history`,
      path: '/symptom-history',
      color: 'indigo',
      available: true
    },
    {
      icon: FileText,
      title: 'Unlimited Exports',
      description: `Generate ${plan?.limits?.monthly_exports === -1 ? 'unlimited' : plan?.limits?.monthly_exports} doctor summaries per month`,
      path: '/doctor-summary-export',
      color: 'teal',
      available: true
    }
  ];

  const activeFeatures = features?.filter(f => f?.available);

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    purple: 'bg-purple-100 text-purple-600 border-purple-200',
    green: 'bg-green-100 text-green-600 border-green-200',
    orange: 'bg-orange-100 text-orange-600 border-orange-200',
    indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
    teal: 'bg-teal-100 text-teal-600 border-teal-200'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Activated Features</h2>
      <p className="text-gray-600 mb-6">
        These Premium features are now available for you to explore and use immediately.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activeFeatures?.map((feature, index) => {
          const IconComponent = feature?.icon;
          return (
            <div 
              key={index}
              className="border-2 border-gray-200 rounded-lg p-5 hover:shadow-md transition-all group"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center border-2 ${colorClasses?.[feature?.color]}`}>
                  <IconComponent className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature?.title}</h3>
                  <p className="text-sm text-gray-600">{feature?.description}</p>
                </div>
              </div>
              <Button 
                onClick={() => onExploreFeature(feature)}
                variant="outline"
                size="sm"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                className="group-hover:bg-blue-50 group-hover:border-blue-300"
              >
                Try Now
              </Button>
            </div>
          );
        })}
      </div>
      {/* Feature Limits Summary */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Plan Limits</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
            <p className="text-3xl font-bold text-blue-600">
              {plan?.limits?.symptom_history_days === -1 ? '∞' : plan?.limits?.symptom_history_days}
            </p>
            <p className="text-sm text-gray-700 mt-1 font-medium">Days of History</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
            <p className="text-3xl font-bold text-green-600">
              {plan?.limits?.monthly_exports === -1 ? '∞' : plan?.limits?.monthly_exports}
            </p>
            <p className="text-sm text-gray-700 mt-1 font-medium">Monthly Exports</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
            <p className="text-3xl font-bold text-purple-600">
              {plan?.limits?.caregiver_connections === -1 ? '∞' : plan?.limits?.caregiver_connections}
            </p>
            <p className="text-sm text-gray-700 mt-1 font-medium">Caregiver Connections</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureActivationCards;